import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import aircraftService from '../../services/aircraftService';

// Async thunks - matching PHP implementation only
export const fetchAircraft = createAsyncThunk(
  'aircraft/fetchAircraft',
  async (params, { rejectWithValue }) => {
    try {
      const response = await aircraftService.getAircraft(params);
      // The API interceptor returns response.data, which contains {success, data, pagination}
      return response; // Return the full response with pagination
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAircraftByUser = createAsyncThunk(
  'aircraft/fetchAircraftByUser',
  async ({ userId, ...params }, { rejectWithValue }) => {
    try {
      const response = await aircraftService.getAircraftByUser(userId, params);
      // The API interceptor returns response.data, which contains {success, data, pagination}
      return response; // This is the full response object with pagination
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAircraftById = createAsyncThunk(
  'aircraft/fetchAircraftById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await aircraftService.getAircraftById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCreateData = createAsyncThunk(
  'aircraft/fetchCreateData',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await aircraftService.getCreateData(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createAircraft = createAsyncThunk(
  'aircraft/createAircraft',
  async (data, { rejectWithValue }) => {
    try {
      const response = await aircraftService.createAircraft(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateAircraft = createAsyncThunk(
  'aircraft/updateAircraft',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await aircraftService.updateAircraft(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAircraft = createAsyncThunk(
  'aircraft/deleteAircraft',
  async (id, { rejectWithValue }) => {
    try {
      const response = await aircraftService.deleteAircraft(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const permanentDeleteAircraft = createAsyncThunk(
  'aircraft/permanentDelete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await aircraftService.permanentDelete(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const restoreAircraft = createAsyncThunk(
  'aircraft/restoreAircraft',
  async (id, { rejectWithValue }) => {
    try {
      const response = await aircraftService.restoreAircraft(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAircraftImage = createAsyncThunk(
  'aircraft/deleteImage',
  async (imageData, { rejectWithValue }) => {
    try {
      const response = await aircraftService.deleteImage(imageData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSeatingLayoutImage = createAsyncThunk(
  'aircraft/deleteSeatingLayoutImage',
  async (id, { rejectWithValue }) => {
    try {
      const response = await aircraftService.deleteSeatingLayoutImage(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAircraftRoutes = createAsyncThunk(
  'aircraft/fetchRoutes',
  async (id, { rejectWithValue }) => {
    try {
      const response = await aircraftService.getRoutes(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAircraftRoute = createAsyncThunk(
  'aircraft/deleteRoute',
  async (routeData, { rejectWithValue }) => {
    try {
      const response = await aircraftService.deleteRoute(routeData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const checkAircraftRegistration = createAsyncThunk(
  'aircraft/checkRegistration',
  async (registrationNumber, { rejectWithValue }) => {
    try {
      const response = await aircraftService.checkRegistration(registrationNumber);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const checkRouteExists = createAsyncThunk(
  'aircraft/checkRouteExists',
  async (params, { rejectWithValue }) => {
    try {
      const response = await aircraftService.checkRouteExists(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAircraftModels = createAsyncThunk(
  'aircraft/fetchModels',
  async (search, { rejectWithValue }) => {
    try {
      const response = search
        ? await aircraftService.searchAircraftModels(search)
        : await aircraftService.getAircraftModels();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const exportAircraft = createAsyncThunk(
  'aircraft/export',
  async (format, { rejectWithValue }) => {
    try {
      const response = await aircraftService.exportAircraft(format);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAircraftImages = createAsyncThunk(
  'aircraft/fetchImages',
  async (id, { rejectWithValue }) => {
    try {
      const response = await aircraftService.getImages(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSeatingLayout = createAsyncThunk(
  'aircraft/fetchSeatingLayout',
  async (id, { rejectWithValue }) => {
    try {
      const response = await aircraftService.getSeatingLayout(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAircraftBookings = createAsyncThunk(
  'aircraft/fetchBookings',
  async (id, { rejectWithValue }) => {
    try {
      const response = await aircraftService.getBookings(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createAircraftRoute = createAsyncThunk(
  'aircraft/createRoute',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await aircraftService.createRoute(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateAircraftRoute = createAsyncThunk(
  'aircraft/updateRoute',
  async ({ routeId, data }, { rejectWithValue }) => {
    try {
      const response = await aircraftService.updateRoute(routeId, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAircraftRouteById = createAsyncThunk(
  'aircraft/deleteRouteById',
  async (routeId, { rejectWithValue }) => {
    try {
      const response = await aircraftService.deleteRouteById(routeId);
      return { routeId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRouteBookings = createAsyncThunk(
  'aircraft/fetchRouteBookings',
  async (routeId, { rejectWithValue }) => {
    try {
      const response = await aircraftService.getRouteBookings(routeId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  aircraft: [],
  currentAircraft: null,
  routes: [],
  aircraftModels: [],
  createData: null,
  images: [],
  seatingLayout: null,
  bookings: [],
  routeBookings: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    perPage: 10
  }
};

// Slice
const aircraftSlice = createSlice({
  name: 'aircraft',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentAircraft: (state) => {
      state.currentAircraft = null;
      state.routes = [];
    },
    setCurrentAircraft: (state, action) => {
      state.currentAircraft = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch aircraft list
      .addCase(fetchAircraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAircraft.fulfilled, (state, action) => {
        state.loading = false;
        state.aircraft = action.payload.data || action.payload;
        // Update pagination if provided
        if (action.payload.pagination) {
          state.pagination = {
            currentPage: action.payload.pagination.currentPage,
            totalPages: action.payload.pagination.totalPages,
            totalRecords: action.payload.pagination.totalRecords,
            perPage: action.payload.pagination.perPage
          };
        }
      })
      .addCase(fetchAircraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch aircraft by user
      .addCase(fetchAircraftByUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAircraftByUser.fulfilled, (state, action) => {
        state.loading = false;
        state.aircraft = action.payload.data || action.payload;
        // Update pagination if provided
        if (action.payload.pagination) {
          state.pagination = {
            currentPage: action.payload.pagination.currentPage,
            totalPages: action.payload.pagination.totalPages,
            totalRecords: action.payload.pagination.totalRecords,
            perPage: action.payload.pagination.perPage
          };
        }
      })
      .addCase(fetchAircraftByUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch single aircraft
      .addCase(fetchAircraftById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAircraftById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAircraft = action.payload.data?.aircraft || action.payload.aircraft || action.payload.data || action.payload;
        state.routes = action.payload.data?.flights || action.payload.flights || [];
        if (action.payload.data?.pagination) {
          state.pagination = action.payload.data.pagination;
        }
      })
      .addCase(fetchAircraftById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch create data
      .addCase(fetchCreateData.fulfilled, (state, action) => {
        state.createData = action.payload.data || action.payload;
      })

      // Create aircraft
      .addCase(createAircraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAircraft.fulfilled, (state, action) => {
        state.loading = false;
        const newAircraft = action.payload.data || action.payload;
        state.aircraft.unshift(newAircraft);
      })
      .addCase(createAircraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update aircraft
      .addCase(updateAircraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAircraft.fulfilled, (state, action) => {
        state.loading = false;
        const updatedAircraft = action.payload.data || action.payload;
        const index = state.aircraft.findIndex(a => a.id === updatedAircraft.id);
        if (index !== -1) {
          state.aircraft[index] = updatedAircraft;
        }
        if (state.currentAircraft?.id === updatedAircraft.id) {
          state.currentAircraft = updatedAircraft;
        }
      })
      .addCase(updateAircraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete aircraft
      .addCase(deleteAircraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAircraft.fulfilled, (state, action) => {
        state.loading = false;
        // Update the aircraft in the list to set deleted_at
        const aircraftId = action.payload.id;
        const index = state.aircraft.findIndex(a => a.id === aircraftId);
        if (index !== -1) {
          state.aircraft[index] = {
            ...state.aircraft[index],
            deleted_at: new Date().toISOString()
          };
        }
      })
      .addCase(deleteAircraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Permanent delete
      .addCase(permanentDeleteAircraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(permanentDeleteAircraft.fulfilled, (state, action) => {
        state.loading = false;
        state.aircraft = state.aircraft.filter(a => a.id !== action.payload.id);
      })
      .addCase(permanentDeleteAircraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Restore aircraft
      .addCase(restoreAircraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreAircraft.fulfilled, (state, action) => {
        state.loading = false;
        // Update the aircraft in the list to remove deleted_at
        const aircraftId = action.meta.arg;
        const index = state.aircraft.findIndex(a => a.id === aircraftId);
        if (index !== -1) {
          state.aircraft[index] = {
            ...state.aircraft[index],
            deleted_at: null
          };
        }
      })
      .addCase(restoreAircraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Routes
      .addCase(fetchAircraftRoutes.fulfilled, (state, action) => {
        state.routes = action.payload.data?.routes || action.payload.routes || action.payload;
      })

      // Aircraft models
      .addCase(fetchAircraftModels.fulfilled, (state, action) => {
        state.aircraftModels = action.payload.data || action.payload;
      })

      // Images
      .addCase(fetchAircraftImages.fulfilled, (state, action) => {
        state.images = action.payload.data || action.payload;
      })

      // Seating layout
      .addCase(fetchSeatingLayout.fulfilled, (state, action) => {
        state.seatingLayout = action.payload.data || action.payload;
      })

      // Bookings
      .addCase(fetchAircraftBookings.fulfilled, (state, action) => {
        state.bookings = action.payload.data || action.payload;
      })

      // Create route
      .addCase(createAircraftRoute.fulfilled, (state, action) => {
        const newRoute = action.payload.data || action.payload;
        state.routes.unshift(newRoute);
      })

      // Update route
      .addCase(updateAircraftRoute.fulfilled, (state, action) => {
        const updatedRoute = action.payload.data || action.payload;
        const index = state.routes.findIndex(r => r.id === updatedRoute.id);
        if (index !== -1) {
          state.routes[index] = updatedRoute;
        }
      })

      // Delete route by ID
      .addCase(deleteAircraftRouteById.fulfilled, (state, action) => {
        state.routes = state.routes.filter(r => r.id !== action.payload.routeId);
      })

      // Route bookings
      .addCase(fetchRouteBookings.fulfilled, (state, action) => {
        state.routeBookings = action.payload.data?.bookings || action.payload.bookings || action.payload;
      });
  }
});

export const { clearError, clearCurrentAircraft, setCurrentAircraft } = aircraftSlice.actions;
export default aircraftSlice.reducer;
